import { useCallback, useRef, useState } from 'react';
import type { PagingMode } from './pagingMode';
import type { ListParams } from './useListParams';

export interface PageInfo {
	currentPage: number;
	totalPages: number;
	totalCount: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

/** One empty array, so a loading page hands out the same `items` every render. */
const NO_ITEMS: never[] = [];

/** The nearest ancestor that scrolls vertically, or null for the viewport. */
function scrollParent(node: HTMLElement): HTMLElement | null {
	for (let el = node.parentElement; el; el = el.parentElement) {
		const { overflowY } = getComputedStyle(el);
		if (overflowY === 'auto' || overflowY === 'scroll') return el;
	}
	return null;
}

/** The `{ data, pageInfo }` shape every paginated query in the API returns. */
export interface PagedResult<TItem> {
	data: TItem[];
	pageInfo: PageInfo;
}

/** What `ListFooter` needs — everything `usePagedList` returns except the rows. */
export interface PagedListControls {
	mode: PagingMode;
	pageInfo: PageInfo | null;
	limit: number;
	loadedCount: number;
	setPage: (page: number) => void;
	loadingMore: boolean;
	hasMore: boolean;
	loadMore: () => void;
	sentinelRef: (node: HTMLElement | null) => void;
}

/**
 * The rows to render for a list driven by `useListParams`, in either mode.
 *
 * Pages mode passes the current page straight through. Scroll mode keeps the
 * rows across requests: a result for page 1 replaces them (first load, filter
 * or sort change, rebuild), any later page is appended. Rows live in React
 * state rather than Apollo's cache, so this works with the `no-cache` policy
 * every list query uses.
 *
 * `loading` is true only when the list is being replaced — show the skeleton
 * for that. `loadingMore` is an append; keep the rows and show the footer
 * spinner.
 *
 * @example
 * const list = useListParams();
 * const { data, loading, refetch } = useGetAllProductsQuery({
 * 	variables: { input: { pagination: list.request } },
 * 	fetchPolicy: 'no-cache',
 * });
 * const products = usePagedList(list, data?.getAllProduct, loading);
 * // render products.items; <ListFooter list={products} />; after a mutation list.reload(refetch)
 */
export function usePagedList<TItem extends { id: string }>(
	params: Pick<ListParams, 'mode' | 'request' | 'limit' | 'setPage' | 'loadMore'>,
	result: PagedResult<TItem> | null | undefined,
	loading: boolean
) {
	const { mode, request, limit, setPage, loadMore } = params;
	const [accumulated, setAccumulated] = useState<TItem[]>(() => result?.data ?? []);
	const [seen, setSeen] = useState({ result, mode });

	// Adjusting state while rendering rather than in an effect, so the new rows
	// show in the same render the result arrives in.
	if (result && (result !== seen.result || mode !== seen.mode)) {
		setSeen({ result, mode });
		if (result.pageInfo.currentPage === 1) {
			setAccumulated(result.data);
		} else if (mode !== seen.mode) {
			// The last page of pages mode, still on screen while switching —
			// not something to append to.
			setAccumulated([]);
		} else {
			const known = new Set(accumulated.map((item) => item.id));
			setAccumulated([...accumulated, ...result.data.filter((item) => !known.has(item.id))]);
		}
	}

	const items = mode === 'pages' ? (result?.data ?? NO_ITEMS) : accumulated;
	const appending = mode === 'scroll' && request.currentPage > 1;
	// `reload` after appending: page 1 sized to every loaded row, with those rows
	// still on screen. A refresh, not a new list — no skeleton, no lost scroll.
	// (A filter change resets to one page's worth, so it still shows the skeleton.)
	const rebuilding =
		mode === 'scroll' &&
		request.currentPage === 1 &&
		request.limit > limit &&
		accumulated.length > 0;
	// A query with new variables has no data until it answers. While appending,
	// the rows above are still there, so keep the footer (and its spinner) too.
	const pageInfo = result?.pageInfo ?? (appending ? (seen.result?.pageInfo ?? null) : null);
	const hasMore = pageInfo?.hasNextPage ?? false;
	const canLoad = mode === 'scroll' && hasMore && !loading;

	const loadMoreRef = useRef(loadMore);
	loadMoreRef.current = loadMore;

	const observer = useRef<IntersectionObserver | null>(null);
	// Re-attached whenever `canLoad` flips back on after a load, so a sentinel
	// still in view (short pages, tall screens) keeps pulling the next page.
	const sentinelRef = useCallback(
		(node: HTMLElement | null) => {
			observer.current?.disconnect();
			if (!node || !canLoad) return;

			observer.current = new IntersectionObserver(
				(entries) => {
					if (!entries[0].isIntersecting) return;
					// One request per sighting; the next observer comes with the next result.
					observer.current?.disconnect();
					loadMoreRef.current();
				},
				// Fires a full screen (100% of the scroller's height) before the end, so the
				// next batch is usually in before anyone reaches it. The margin only works
				// against the element that actually scrolls; against the viewport (the
				// default root) the layout's scroll container clips it away.
				{ root: scrollParent(node), rootMargin: '100% 0px' }
			);
			observer.current.observe(node);
		},
		[canLoad]
	);

	return {
		mode,
		items,
		pageInfo,
		limit,
		loadedCount: items.length,
		setPage,
		loading: loading && !appending && !rebuilding,
		loadingMore: loading && appending,
		hasMore,
		loadMore,
		sentinelRef,
	} satisfies PagedListControls & { items: TItem[]; loading: boolean };
}
